import { useAtomValue } from 'jotai';
import {
  Column,
  FormGroup,
  FlexGrid as Grid,
  Row,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tile,
} from '@carbon/react';
import { DiscountRateAssumptions } from './forms/discount-rate-assumptions';
import { FormRow } from 'components/forms';
import { AtomizedForm } from 'atoms/form-atoms';
import { TierAssumptionsForm } from './types';
import { NumberField } from 'components/form-fields/number-field';

import style from './scenario-builder.module.scss';
import { TierFormAtom } from './atoms';
import { InfillAssumptions } from './forms/infill-assumptions';
import { VariableCosts } from './forms/variable-costs';
import { FixedCosts } from './forms/fixed-costs';
import { EconomicLimits } from './forms/economic-limits';

const CashflowAssumptions = ({
  form,
}: {
  form: AtomizedForm<TierAssumptionsForm[0]['cashflowAssumptionAttributes']>;
}) => {
  return (
    <>
      <Grid className="bx--no-gutter" fullWidth condensed>
        <Row className={style.rowThirds}>
          <Column>
            <Tile>
              <FormGroup legendText="Reservoir Assumptions">
                <FormRow>
                  <NumberField
                    light={true}
                    id="gasShrinkPercentage"
                    label="Gas Shrink (%)"
                    min={0}
                    invalidText="Must be positive number"
                    field={
                      form.reservoirAssumptionAttributes.gasShrinkPercentage
                    }
                  />
                </FormRow>
                <FormRow>
                  <NumberField
                    light={true}
                    id="nglYield"
                    label="NGL Yield (BBL/MMCF)"
                    min={0}
                    invalidText="Must be positive number"
                    field={form.reservoirAssumptionAttributes.nglYield}
                  />
                </FormRow>
              </FormGroup>
              <EconomicLimits form={form} />
              <FormGroup legendText="Royalty Rates and Burdens">
                <FormRow>
                  <NumberField
                    light={true}
                    id="royaltyPercentage"
                    label="Royalty Rate (%)"
                    field={form.royaltyPercentage}
                  />
                </FormRow>
                <FormRow>
                  <NumberField
                    light={true}
                    id="orriPercentage"
                    label="ORRI (%)"
                    field={form.orriPercentage}
                  />
                </FormRow>
              </FormGroup>
            </Tile>
          </Column>
          <Column>
            <Tile>
              <FormGroup legendText="Differentials">
                <FormRow>
                  <NumberField
                    light={true}
                    id="oilDifferentialPrice"
                    label="Oil ($/BBL)"
                    field={form.differentialAttributes.oilPrice}
                  />
                </FormRow>
                <FormRow>
                  <NumberField
                    light={true}
                    id="gasDifferentialPrice"
                    label="Gas ($/MMBTU)"
                    field={form.differentialAttributes.gasPrice}
                  />
                </FormRow>
                <FormRow>
                  <NumberField
                    light
                    id="nglDifferentialPercentage"
                    label="NGL (%)"
                    min={0}
                    invalidText="Must be positive number"
                    field={form.differentialAttributes.nglPercentage}
                  />
                </FormRow>
              </FormGroup>
            </Tile>
          </Column>
        </Row>
        <Row>
          <Column>
            <Tile>
              <VariableCosts form={form} />
            </Tile>
          </Column>
          <Column>
            <Tile>
              <FixedCosts form={form} />
            </Tile>
          </Column>
        </Row>
      </Grid>
    </>
  );
};

const TierForm = ({ formAtom }: { formAtom: TierFormAtom }) => {
  const tierForm = useAtomValue(formAtom);
  if (!tierForm) return null;

  return (
    <div className={style.tabsContained}>
      <Tabs>
        <TabList contained>
          <Tab>Drilling Assumptions</Tab>
          <Tab>Cashflow Assumptions</Tab>
          <Tab>Discount Rates Assumptions</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Tile>
              <div className={style.infillFields}>
                <InfillAssumptions
                  form={tierForm.drillingAssumptionAttributes.infillAttributes}
                />
              </div>
              <FormRow>
                <NumberField
                  light={true}
                  id="averageWellDrilledYears"
                  label="Average Well Drilled (Years)"
                  field={
                    tierForm.drillingAssumptionAttributes.ltdAttributes
                      .averageWellDrilledYears
                  }
                  min={0}
                />
              </FormRow>
              <FormRow>
                <NumberField
                  light={true}
                  id="drillStartMonths"
                  label="Drill Start (Months)"
                  field={
                    tierForm.drillingAssumptionAttributes.ltdAttributes
                      .drillStartMonths
                  }
                  min={0}
                />
              </FormRow>
              <FormRow>
                <NumberField
                  light={true}
                  id="spudToProductionMonths"
                  label="Spud To Production (Months)"
                  field={
                    tierForm.drillingAssumptionAttributes.ltdAttributes
                      .spudToProductionMonths
                  }
                  min={0}
                />
              </FormRow>
            </Tile>
          </TabPanel>
          <TabPanel>
            <CashflowAssumptions form={tierForm.cashflowAssumptionAttributes} />
          </TabPanel>
          <TabPanel>
            <DiscountRateAssumptions
              form={tierForm.cashflowAssumptionAttributes}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

export { TierForm };
